/**
 * Roster typeahead source for the Phase 3b TeamChipPicker.
 *
 * `GET /api/admin/team-roster?role=investment` returns the active
 * investment team (the typical deal-team membership). Omit the
 * `role` param to get the full active roster.
 *
 * 1hr stale-time matches the cadence of roster edits — adding a
 * new teammate via the Admin tab is rare enough that a cached
 * picker is fine.
 */
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '../api/client';

export interface TeamRosterMember {
  email: string;
  name: string | null;
  short_name: string | null;
  title: string | null;
  team_classification: string | null;
  seniority_rank: number | null;
}

export interface TeamRosterResponse {
  members: TeamRosterMember[];
}

export function useTeamRoster(role: string | null = 'investment') {
  const params = new URLSearchParams();
  if (role) params.set('role', role);
  const qs = params.toString();
  return useQuery<TeamRosterResponse>({
    queryKey: ['admin', 'team-roster', role ?? 'all'],
    queryFn: () =>
      apiFetch<TeamRosterResponse>(
        `/api/admin/team-roster${qs ? `?${qs}` : ''}`,
      ),
    staleTime: 60 * 60 * 1000,
  });
}
